import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../../organisation/useMe';
import { internalRiskRuleApi } from '../../apis';
import { InternalApiRiskRule, InternalApiRiskRuleUpdate, InternalApiRiskRuleVersionUpdate } from '../../api-internal';

export const RISK_RULE_QUERY_KEY = 'risk_rule';

export const useQueryRiskRule = (id: string) => {
  const { currentOrganisation } = useMe();

  return useQuery({
    queryKey: [RISK_RULE_QUERY_KEY, id, currentOrganisation?.id],
    queryFn: async () => await internalRiskRuleApi.getRiskRule({ handle: id }),
  });
};

export const useUpdateRiskRule = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: ({ handle, update }: { handle: string; update: InternalApiRiskRuleUpdate }) => {
      return internalRiskRuleApi.updateRiskRule({
        handle: handle,
        internalApiRiskRuleUpdate: update,
      });
    },
    onSuccess: (riskRule: InternalApiRiskRule) => {
      queryClient.setQueryData([RISK_RULE_QUERY_KEY, riskRule.id, currentOrganisation?.id], riskRule);
    },
  });
};

export const useUpdateRiskRuleVersion = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: ({ handle, id, update }: { handle: string; id: number; update: InternalApiRiskRuleVersionUpdate }) => {
      return internalRiskRuleApi.updateVersion({
        handle: handle,
        id: id,
        internalApiRiskRuleVersionUpdate: update,
      });
    },
    onSuccess: (riskRule: InternalApiRiskRule) => {
      queryClient.setQueryData([RISK_RULE_QUERY_KEY, riskRule.id, currentOrganisation?.id], riskRule);
    },
  });
};

export const useAddRiskRuleVersion = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: ({ handle, update }: { handle: string; update: InternalApiRiskRuleVersionUpdate }) => {
      return internalRiskRuleApi.addNewVersion({ handle: handle, internalApiRiskRuleVersionUpdate: update });
    },
    onSuccess: (riskRule: InternalApiRiskRule) => {
      queryClient.setQueryData([RISK_RULE_QUERY_KEY, riskRule.id, currentOrganisation?.id], riskRule);
    },
  });
};

export const useDuplicateRiskRuleVersion = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: ({ handle, id }: { handle: string; id: number }) => {
      return internalRiskRuleApi.duplicateVersion({ handle: handle, id: id });
    },
    onSuccess: (riskRule: InternalApiRiskRule) => {
      queryClient.setQueryData([RISK_RULE_QUERY_KEY, riskRule.id, currentOrganisation?.id], riskRule);
    },
  });
};
